import React, { useState } from 'react';
import { useRelatorioVendas } from '../../hooks/useRelatorios';

const RelatorioVendas: React.FC = () => {
  const hoje = new Date();
  const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);

  const [dataInicio, setDataInicio] = useState<string>(
    primeiroDiaMes.toISOString().split('T')[0]
  );
  const [dataFim, setDataFim] = useState<string>(hoje.toISOString().split('T')[0]);
  const [relatorioAtivo, setRelatorioAtivo] = useState(false);

  const { data: relatorio, isLoading, error } = useRelatorioVendas(
    dataInicio ? `${dataInicio}T00:00:00` : '',
    dataFim ? `${dataFim}T23:59:59` : '',
    relatorioAtivo
  );

  const handleGerarRelatorio = () => {
    if (dataInicio && dataFim) {
      setRelatorioAtivo(true);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateString));
  };

  return (
    <>
      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Período do Relatório</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Início</label>
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Fim</label>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleGerarRelatorio}
              disabled={!dataInicio || !dataFim}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Gerar Relatório
            </button>
          </div>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">Carregando relatório...</div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-600">Erro ao carregar relatório. Tente novamente.</p>
        </div>
      )}

      {/* Relatório */}
      {relatorio && !isLoading && (
        <>
          {/* Cards de Resumo */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-600 mb-1">Total Geral</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(relatorio.totalGeral)}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                {relatorio.quantidadePedidos + relatorio.quantidadeEncomendas} transações
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-600 mb-1">Vendas PDV</p>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(relatorio.totalVendas)}
              </p>
              <p className="text-xs text-gray-500 mt-2">{relatorio.quantidadePedidos} pedidos</p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-600 mb-1">Encomendas</p>
              <p className="text-2xl font-bold text-purple-600">
                {formatCurrency(relatorio.totalEncomendas)}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                {relatorio.quantidadeEncomendas} encomendas
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-600 mb-1">Ticket Médio</p>
              <p className="text-2xl font-bold text-orange-600">
                {formatCurrency(
                  relatorio.quantidadePedidos + relatorio.quantidadeEncomendas > 0
                    ? relatorio.totalGeral /
                        (relatorio.quantidadePedidos + relatorio.quantidadeEncomendas)
                    : 0
                )}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                PDV: {formatCurrency(relatorio.ticketMedioPedidos)} | Enc:{' '}
                {formatCurrency(relatorio.ticketMedioEncomendas)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Vendas por Método de Pagamento */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Vendas por Método de Pagamento
              </h2>
              {relatorio.vendasPorMetodoPagamento.length > 0 ? (
                <div className="space-y-3">
                  {relatorio.vendasPorMetodoPagamento.map((metodo, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">
                            {metodo.metodoPagamento}
                          </span>
                          <span className="text-sm text-gray-500">{metodo.quantidade} vendas</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${metodo.percentual}%` }}
                          ></div>
                        </div>
                      </div>
                      <span className="ml-4 text-sm font-bold text-gray-800">
                        {formatCurrency(metodo.valorTotal)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">Nenhuma venda no período</p>
              )}
            </div>

            {/* Top Produtos */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Produtos Mais Vendidos
              </h2>
              {relatorio.produtosMaisVendidos.length > 0 ? (
                <div className="space-y-3">
                  {relatorio.produtosMaisVendidos.slice(0, 5).map((produto, index) => (
                    <div
                      key={produto.produtoId}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                          {index + 1}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">
                            {produto.nomeProduto}
                          </p>
                          <p className="text-xs text-gray-500">
                            {produto.quantidadeVendida} unidades
                          </p>
                        </div>
                      </div>
                      <p className="text-sm font-bold text-gray-800">
                        {formatCurrency(produto.valorTotal)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">Nenhuma venda no período</p>
              )}
            </div>
          </div>

          {/* Top Clientes */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Top 10 Clientes</h2>
            {relatorio.topClientes.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        #
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Cliente
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Compras
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {relatorio.topClientes.map((cliente, index) => (
                      <tr key={cliente.clienteId} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-800">{index + 1}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-800">
                          {cliente.nomeCliente}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 text-center">
                          {cliente.quantidadeCompras}
                        </td>
                        <td className="px-4 py-3 text-sm font-bold text-gray-800 text-right">
                          {formatCurrency(cliente.valorTotal)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Nenhum cliente no período</p>
            )}
          </div>

          {/* Vendas por Dia */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Vendas por Dia</h2>
            {relatorio.vendasPorDia.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Data
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        PDV
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Encomendas
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Total
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Qtd.
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {relatorio.vendasPorDia.map((dia) => (
                      <tr key={dia.data} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-800">
                          {formatDate(dia.data)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 text-right">
                          {formatCurrency(dia.totalPedidos)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 text-right">
                          {formatCurrency(dia.totalEncomendas)}
                        </td>
                        <td className="px-4 py-3 text-sm font-bold text-gray-800 text-right">
                          {formatCurrency(dia.total)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 text-center">
                          {dia.quantidadePedidos + dia.quantidadeEncomendas}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 font-bold">
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-800">TOTAL</td>
                      <td className="px-4 py-3 text-sm text-gray-800 text-right">
                        {formatCurrency(
                          relatorio.vendasPorDia.reduce((sum, dia) => sum + dia.totalPedidos, 0)
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-800 text-right">
                        {formatCurrency(
                          relatorio.vendasPorDia.reduce((sum, dia) => sum + dia.totalEncomendas, 0)
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-800 text-right">
                        {formatCurrency(
                          relatorio.vendasPorDia.reduce((sum, dia) => sum + dia.total, 0)
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-800 text-center">
                        {relatorio.vendasPorDia.reduce(
                          (sum, dia) => sum + dia.quantidadePedidos + dia.quantidadeEncomendas,
                          0
                        )}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Nenhuma venda no período</p>
            )}
          </div>
        </>
      )}

      {/* Mensagem inicial */}
      {!relatorioAtivo && !isLoading && (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 text-center py-8">
            Selecione um período e clique em "Gerar Relatório" para visualizar os dados
          </p>
        </div>
      )}
    </>
  );
};

export default RelatorioVendas;
